import { useState, useEffect, useCallback, memo, useRef } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from 'react-simple-maps';
import { scaleLinear } from 'd3-scale';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import './countryDistributionMap.css';

// TopoJSON URL for world map (hosted by react-simple-maps)
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Country code to name mapping for tooltips
const COUNTRY_NAMES = {
  US: 'United States', DE: 'Germany', GB: 'United Kingdom', FR: 'France',
  IT: 'Italy', ES: 'Spain', NL: 'Netherlands', BE: 'Belgium', AT: 'Austria',
  CH: 'Switzerland', PL: 'Poland', SE: 'Sweden', NO: 'Norway', DK: 'Denmark',
  BA: 'Bosnia and Herzegovina', ZM: 'Zambia',
  FI: 'Finland', PT: 'Portugal', IE: 'Ireland', CZ: 'Czech Republic',
  RO: 'Romania', HU: 'Hungary', GR: 'Greece', SK: 'Slovakia', BG: 'Bulgaria',
  HR: 'Croatia', SI: 'Slovenia', LT: 'Lithuania', LV: 'Latvia', EE: 'Estonia',
  CY: 'Cyprus', LU: 'Luxembourg', MT: 'Malta', CA: 'Canada', MX: 'Mexico',
  BR: 'Brazil', AR: 'Argentina', CL: 'Chile', CO: 'Colombia', PE: 'Peru',
  VE: 'Venezuela', AU: 'Australia', NZ: 'New Zealand', JP: 'Japan',
  CN: 'China', IN: 'India', KR: 'South Korea', SG: 'Singapore', MY: 'Malaysia',
  TH: 'Thailand', ID: 'Indonesia', PH: 'Philippines', VN: 'Vietnam',
  RU: 'Russia', UA: 'Ukraine', TR: 'Turkey', SA: 'Saudi Arabia', AE: 'UAE',
  IL: 'Israel', EG: 'Egypt', ZA: 'South Africa', NG: 'Nigeria', KE: 'Kenya',
  MA: 'Morocco', PK: 'Pakistan', BD: 'Bangladesh', TW: 'Taiwan', HK: 'Hong Kong'
};

// Localized country name via the browser's Intl.DisplayNames (auto-translated per
// locale), falling back to the English table above and then the raw code.
const _regionDisplay = {};
function countryName(code, lng) {
  if (!code) return code;
  const cc = String(code).toUpperCase();
  try {
    const key = lng || 'en';
    if (!_regionDisplay[key]) _regionDisplay[key] = new Intl.DisplayNames([key], { type: 'region' });
    const name = _regionDisplay[key].of(cc);
    if (name && name !== cc) return name;
  } catch (e) {
    /* unsupported locale/code — fall through to static table */
  }
  return COUNTRY_NAMES[cc] || code;
}

// ISO Alpha-2 to ISO 3166-1 numeric mapping for TopoJSON matching
const ALPHA2_TO_NUMERIC = {
  AF: '004', AL: '008', DZ: '012', AS: '016', AD: '020', AO: '024', AG: '028',
  AR: '032', AM: '051', AU: '036', AT: '040', AZ: '031', BS: '044', BH: '048',
  BD: '050', BB: '052', BY: '112', BE: '056', BZ: '084', BJ: '204', BT: '064',
  BO: '068', BA: '070', BW: '072', BR: '076', BN: '096', BG: '100', BF: '854',
  BI: '108', KH: '116', CM: '120', CA: '124', CV: '132', CF: '140', TD: '148',
  CL: '152', CN: '156', CO: '170', KM: '174', CG: '178', CD: '180', CR: '188',
  CI: '384', HR: '191', CU: '192', CY: '196', CZ: '203', DK: '208', DJ: '262',
  DM: '212', DO: '214', EC: '218', EG: '818', SV: '222', GQ: '226', ER: '232',
  EE: '233', ET: '231', FJ: '242', FI: '246', FR: '250', GA: '266', GM: '270',
  GE: '268', DE: '276', GH: '288', GR: '300', GD: '308', GT: '320', GN: '324',
  GW: '624', GY: '328', HT: '332', HN: '340', HU: '348', IS: '352', IN: '356',
  ID: '360', IR: '364', IQ: '368', IE: '372', IL: '376', IT: '380', JM: '388',
  JP: '392', JO: '400', KZ: '398', KE: '404', KI: '296', KP: '408', KR: '410',
  KW: '414', KG: '417', LA: '418', LV: '428', LB: '422', LS: '426', LR: '430',
  LY: '434', LI: '438', LT: '440', LU: '442', MK: '807', MG: '450', MW: '454',
  MY: '458', MV: '462', ML: '466', MT: '470', MH: '584', MR: '478', MU: '480',
  MX: '484', FM: '583', MD: '498', MC: '492', MN: '496', ME: '499', MA: '504',
  MZ: '508', MM: '104', NA: '516', NR: '520', NP: '524', NL: '528', NZ: '554',
  NI: '558', NE: '562', NG: '566', NO: '578', OM: '512', PK: '586', PW: '585',
  PA: '591', PG: '598', PY: '600', PE: '604', PH: '608', PL: '616', PT: '620',
  QA: '634', RO: '642', RU: '643', RW: '646', KN: '659', LC: '662', VC: '670',
  WS: '882', SM: '674', ST: '678', SA: '682', SN: '686', RS: '688', SC: '690',
  SL: '694', SG: '702', SK: '703', SI: '705', SB: '090', SO: '706', ZA: '710',
  SS: '728', ES: '724', LK: '144', SD: '729', SR: '740', SZ: '748', SE: '752',
  CH: '756', SY: '760', TW: '158', TJ: '762', TZ: '834', TH: '764', TL: '626',
  TG: '768', TO: '776', TT: '780', TN: '788', TR: '792', TM: '795', TV: '798',
  UG: '800', UA: '804', AE: '784', GB: '826', US: '840', UY: '858', UZ: '860',
  VU: '548', VE: '862', VN: '704', YE: '887', ZM: '894', ZW: '716'
};

// Country center coordinates for zoom-to-country feature
const COUNTRY_CENTERS = {
  US: [-95, 38], DE: [10, 51], GB: [-2, 54], FR: [2, 47], IT: [12, 43],
  ES: [-4, 40], NL: [5, 52], BE: [4, 51], AT: [14, 47], CH: [8, 47],
  BA: [18, 44], ZM: [28, -15],
  PL: [19, 52], SE: [15, 62], NO: [9, 62], DK: [10, 56], FI: [26, 64],
  PT: [-8, 39], IE: [-8, 53], CZ: [15, 50], RO: [25, 46], HU: [19, 47],
  GR: [22, 39], SK: [19, 49], BG: [25, 43], HR: [16, 45], SI: [15, 46],
  LT: [24, 55], LV: [25, 57], EE: [25, 59], CY: [33, 35], LU: [6, 50],
  MT: [14, 36], CA: [-106, 56], MX: [-102, 24], BR: [-53, -10],
  AR: [-64, -34], CL: [-71, -35], CO: [-72, 4], PE: [-76, -10],
  VE: [-67, 8], AU: [134, -25], NZ: [174, -41], JP: [138, 36],
  CN: [105, 35], IN: [79, 22], KR: [128, 36], SG: [104, 1], MY: [102, 4],
  TH: [101, 15], ID: [120, -2], PH: [122, 12], VN: [108, 16],
  RU: [100, 60], UA: [32, 49], TR: [35, 39], SA: [45, 24], AE: [54, 24],
  IL: [35, 31], EG: [30, 27], ZA: [25, -29], NG: [8, 10], KE: [38, 0],
  MA: [-6, 32], PK: [69, 30], BD: [90, 24], TW: [121, 24], HK: [114, 22]
};

const CountryDistributionMap = ({
  selectedBotIds = [],
  startDate,
  endDate
}) => {
  const { t, i18n } = useTranslation();
  const [countryData, setCountryData] = useState({});
  const [totalSessions, setTotalSessions] = useState(0);
  const [sessionsWithIp, setSessionsWithIp] = useState(0);
  const [noIpCount, setNoIpCount] = useState(0);
  const [unknownCount, setUnknownCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [position, setPosition] = useState({ coordinates: [10, 45], zoom: 1.8 });
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [highlightedCountry, setHighlightedCountry] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Ref to track the current request and abort stale ones
  const abortControllerRef = useRef(null);

  const fetchCountryStats = useCallback(async () => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    if (!selectedBotIds.length) {
      setCountryData({});
      setTotalSessions(0);
      setSessionsWithIp(0);
      setNoIpCount(0);
      setUnknownCount(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const botIdsParam = selectedBotIds.join(',');
      const url = `${process.env.REACT_APP_API_BASE_URL}chat_history_country_stats?bot_ids=${botIdsParam}&start_date=${startDate}&end_date=${endDate}`;

      const response = await fetch(url, { signal: abortController.signal });

      // Check if this request was aborted
      if (abortController.signal.aborted) return;

      if (!response.ok) throw new Error('Failed to fetch country stats');

      const data = await response.json();

      // Double-check abort status before updating state
      if (abortController.signal.aborted) return;

      setCountryData(data.countries || {});
      setTotalSessions(data.total_sessions || 0);
      setSessionsWithIp(data.sessions_with_ip || 0);
      setNoIpCount(data.no_ip || 0);
      setUnknownCount((data.unknown || 0) + (data.no_ip || 0));
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') return;

      console.error('Error fetching country stats:', err);
      setCountryData({});
      setTotalSessions(0);
      setSessionsWithIp(0);
      setNoIpCount(0);
      setUnknownCount(0);
    } finally {
      // Only set loading false if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [selectedBotIds, startDate, endDate]);

  useEffect(() => {
    fetchCountryStats();

    // Cleanup: abort on unmount or when dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchCountryStats]);

  // Calculate max value for color scale
  const maxValue = Math.max(...Object.values(countryData), 1);

  // Color scale from light to dark purple
  const colorScale = scaleLinear()
    .domain([0, maxValue])
    .range(['#f0e6ff', '#5616ea']);

  const getCountryColor = (countryCode) => {
    const count = countryData[countryCode] || 0;
    if (count === 0) return '#e5e7eb';
    // Highlighted from legend hover
    if (highlightedCountry === countryCode) return '#3d00cc';
    // Selected from legend click (slightly different shade)
    if (selectedCountry === countryCode) return '#4a10d9';
    return colorScale(count);
  };

  const handleMouseEnter = (geo, evt) => {
    // Try to match by ISO numeric code
    const numericCode = geo.id;
    const alpha2 = Object.keys(ALPHA2_TO_NUMERIC).find(
      key => ALPHA2_TO_NUMERIC[key] === numericCode
    );

    if (alpha2 && countryData[alpha2]) {
      const name = countryName(alpha2, i18n.language);
      const count = countryData[alpha2];
      setTooltipContent(`${name}: ${t('map_country_sessions', { count })}`);
      setTooltipPosition({ x: evt.clientX, y: evt.clientY });
    }
  };

  const handleMouseLeave = () => {
    setTooltipContent('');
  };

  // Zoom and pan handlers
  const handleMoveEnd = (position) => {
    setPosition(position);
  };

  const handleZoomIn = () => {
    if (position.zoom >= 8) return;
    setPosition(pos => ({ ...pos, zoom: pos.zoom * 1.5 }));
  };

  const handleZoomOut = () => {
    if (position.zoom <= 1) return;
    setPosition(pos => ({ ...pos, zoom: pos.zoom / 1.5 }));
  };

  const handleReset = () => {
    setPosition({ coordinates: [10, 45], zoom: 1.8 });
    setSelectedCountry(null);
  };

  // Legend hover handlers
  const handleLegendMouseEnter = (code, evt) => {
    setHighlightedCountry(code);
    const name = countryName(code, i18n.language);
    const count = countryData[code] || 0;
    setTooltipContent(`${name}: ${t('map_country_sessions', { count })}`);
    setTooltipPosition({ x: evt.clientX, y: evt.clientY });
  };

  const handleLegendMouseLeave = () => {
    setHighlightedCountry(null);
    setTooltipContent('');
  };

  // Legend click handler - zoom to country
  const handleLegendClick = (code) => {
    const center = COUNTRY_CENTERS[code];
    if (center) {
      // Toggle selection - if same country clicked, reset view
      if (selectedCountry === code) {
        setPosition({ coordinates: [10, 45], zoom: 1.8 });
        setSelectedCountry(null);
      } else {
        setPosition({ coordinates: center, zoom: 4 });
        setSelectedCountry(code);
      }
    }
  };

  // Get all countries for the legend sorted by count
  const topCountries = Object.entries(countryData)
    .sort(([, a], [, b]) => b - a);

  if (loading) {
    return (
      <div className="country-map-container">
        <div className="charts-title">{t('user_locations') || 'User Locations'}</div>
        <div className="country-map-loading">
          <FontAwesomeIcon icon={faSpinner} spin className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="country-map-container">
      <div className="charts-title">{t('user_locations') || 'User Locations'}</div>
      <div className="country-map-wrapper">
        <div className="country-map-chart">
          <div className="map-controls">
            <button onClick={handleZoomIn} className="map-control-btn" title={t('map_zoom_in') || 'Zoom in'}>+</button>
            <button onClick={handleZoomOut} className="map-control-btn" title={t('map_zoom_out') || 'Zoom out'}>−</button>
            <button onClick={handleReset} className="map-control-btn map-reset-btn" title={t('map_reset') || 'Reset'}>↺</button>
          </div>
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{
              scale: 140,
              center: [0, 20]
            }}
            style={{ width: '100%', height: '100%' }}
          >
            <ZoomableGroup
              zoom={position.zoom}
              center={position.coordinates}
              onMoveEnd={handleMoveEnd}
              minZoom={1}
              maxZoom={8}
              translateExtent={[[-1000, -500], [1000, 500]]}
            >
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    // Find matching alpha-2 code
                    const numericCode = geo.id;
                    const alpha2 = Object.keys(ALPHA2_TO_NUMERIC).find(
                      key => ALPHA2_TO_NUMERIC[key] === numericCode
                    );

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={alpha2 ? getCountryColor(alpha2) : '#e5e7eb'}
                        stroke={alpha2 && (highlightedCountry === alpha2 || selectedCountry === alpha2) ? '#3d00cc' : '#fff'}
                        strokeWidth={alpha2 && (highlightedCountry === alpha2 || selectedCountry === alpha2) ? 1.5 : 0.5}
                        style={{
                          default: { outline: 'none' },
                          hover: {
                            fill: alpha2 && countryData[alpha2] ? '#3d00cc' : '#d1d5db',
                            outline: 'none',
                            cursor: alpha2 && countryData[alpha2] ? 'pointer' : 'default'
                          },
                          pressed: { outline: 'none' }
                        }}
                        onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                        onMouseLeave={handleMouseLeave}
                      />
                    );
                  })
                }
              </Geographies>
            </ZoomableGroup>
          </ComposableMap>

          {tooltipContent && (
            <div
              className="country-map-tooltip"
              style={{
                left: tooltipPosition.x + 10,
                top: tooltipPosition.y - 30
              }}
            >
              {tooltipContent}
            </div>
          )}
        </div>

        <div className="country-map-legend">
          <div className="legend-title">{t('countries') || 'Countries'}</div>
          <div className="legend-items">
            {topCountries.length > 0 ? (
              <>
                {topCountries.map(([code, count]) => (
                  <div
                    key={code}
                    className={`legend-item ${selectedCountry === code ? 'legend-item-selected' : ''} ${highlightedCountry === code ? 'legend-item-highlighted' : ''}`}
                    onMouseEnter={(evt) => handleLegendMouseEnter(code, evt)}
                    onMouseLeave={handleLegendMouseLeave}
                    onClick={() => handleLegendClick(code)}
                  >
                    <div
                      className="legend-color"
                      style={{ backgroundColor: colorScale(count) }}
                    />
                    <span className="legend-country">{countryName(code, i18n.language)}</span>
                    <span className="legend-count">{count}</span>
                  </div>
                ))}
                {unknownCount > 0 && (
                  <div className="legend-item legend-item-unknown">
                    <div
                      className="legend-color"
                      style={{ backgroundColor: '#e5e7eb' }}
                    />
                    <span className="legend-country">{t('unknown') || 'Unknown'}</span>
                    <span className="legend-count">{unknownCount}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="legend-empty">
                <p>{t('no_location_data') || 'No location data available'}</p>
                {totalSessions > 0 && noIpCount > 0 && (
                  <p className="no-ip-hint">
                    {t('map_n_of_m', { count: noIpCount, total: totalSessions })} {t('sessions_no_ip') || 'sessions have no IP'}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="legend-total">
            {t('map_n_of_m', { count: sessionsWithIp, total: totalSessions })} {t('sessions_located') || 'sessions located'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(CountryDistributionMap);
